import { Modal, Form, Input, Select, DatePicker, Row, Col, Tag, Spin } from 'antd';
import { UserOutlined, PhoneOutlined, CalendarOutlined } from '@ant-design/icons';
import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { eventApi } from '../../../api/event.api';
import { childApi } from '../../../api/child.api';
import { useAuthStore } from '../../../store/auth.store';

const RegistrationForm = ({ open, onClose, onSubmit, loading }) => {
  const [form] = Form.useForm();
  const [childrenForPhone, setChildrenForPhone] = useState([]);
  const [selectedChild,    setSelectedChild]    = useState(null);
  const [lookupLoading,    setLookupLoading]    = useState(false);
  const [lastPhone,        setLastPhone]        = useState('');
  const { user } = useAuthStore();
  const onlyDigits = (maxLength) => (e) => e.target.value.replace(/\D/g, '').slice(0, maxLength);

  const { data: eventsData } = useQuery({
    queryKey: ['events-for-registration'],
    queryFn:  () => eventApi.getAll({ limit: 100 }),
    enabled:  open,
  });

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setChildrenForPhone([]);
      setSelectedChild(null);
      setLastPhone('');
    }
  }, [open, form]);

  const handleParentPhoneChange = useCallback(async (e) => {
    const phone = e.target.value.replace(/\D/g, '');
    if (phone.length === 10 && phone !== lastPhone) {
      setLastPhone(phone);
      setLookupLoading(true);
      setSelectedChild(null);
      form.setFieldsValue({ child: undefined });
      try {
        const res = await childApi.getByParentPhone(phone);
        setChildrenForPhone(res.data || []);
        if ((res.data || []).length === 0) {
          form.setFields([{ name: 'parentPhone', errors: ['No child profiles found for this mobile'] }]);
        }
      } catch {
        setChildrenForPhone([]);
      } finally {
        setLookupLoading(false);
      }
    } else if (phone.length < 10) {
      setChildrenForPhone([]);
      setSelectedChild(null);
      form.setFieldsValue({ child: undefined });
      setLastPhone('');
    }
  }, [form, lastPhone]);

  const handleChildSelect = (childId) => {
    const child = childrenForPhone.find((c) => c.id === childId);
    if (child) { setSelectedChild(child); }
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    onSubmit({
      event:            values.event,
      child:            values.child,
      school:           selectedChild?.school?.id || selectedChild?.school,
      registrationDate: values.registrationDate?.toISOString() || new Date().toISOString(),
    });
  };

  const upcomingEvents = (eventsData?.data || []).filter((e) => e.status !== 'cancelled');

  return (
    <Modal
      title={<span style={{ fontFamily: "'Fredoka One', sans-serif", color: 'var(--wl-primary)', fontSize: 16 }}>Register Child for Event</span>}
      open={open} onCancel={onClose} onOk={handleSubmit}
      okText="Register" confirmLoading={loading} width={520} destroyOnClose
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'var(--wl-primary-pale)', borderRadius: 8, padding: '8px 14px',
        marginBottom: 18, border: '1px solid var(--wl-primary-pale)', fontSize: 12, color: 'var(--wl-text-muted)',
      }}>
        <UserOutlined />
        <span>Registering as: <strong>{user?.name}</strong> ({user?.role?.replace('_', ' ')})</span>
      </div>

      <Form form={form} layout="vertical">
        <Form.Item
          name="event"
          label={<span style={{ fontWeight: 600, color: 'var(--wl-primary)' }}>Event Title</span>}
          rules={[{ required: true, message: 'Select an event' }]}
        >
          <Select
            placeholder="Select event"
            showSearch
            optionFilterProp="label"
            options={upcomingEvents.map((e) => ({
              label: `${e.title} — ${e.school?.name || ''} (${dayjs(e.eventDate).format('DD MMM YYYY')})`,
              value: e.id,
            }))}
          />
        </Form.Item>

        <Row gutter={14}>
          <Col span={12}>
            <Form.Item
              name="parentPhone"
              label={<span style={{ fontWeight: 600, color: 'var(--wl-primary)' }}>Parent Mobile Number</span>}
              getValueFromEvent={onlyDigits(10)}
              rules={[{ required: true }, { pattern: /^\d{10}$/, message: 'Must be 10 digits' }]}
              extra="Enter mobile to find child profiles"
            >
              <Input
                prefix={<PhoneOutlined style={{ color: 'var(--wl-text-muted)' }} />}
                placeholder="10-digit mobile"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                onChange={handleParentPhoneChange}
                suffix={lookupLoading ? <Spin size="small" /> : null}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="child"
              label={<span style={{ fontWeight: 600, color: 'var(--wl-primary)' }}>Child Name</span>}
              rules={[{ required: true, message: 'Select the child' }]}
            >
              <Select
                placeholder={childrenForPhone.length === 0 ? 'Enter mobile first' : 'Select child'}
                loading={lookupLoading}
                disabled={childrenForPhone.length === 0 && !lookupLoading}
                onChange={handleChildSelect}
                options={childrenForPhone.map((c) => ({
                  label: `${c.name}${c.age ? ` (${c.age} yrs)` : ''}`,
                  value: c.id,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="registrationDate"
          label={<span style={{ fontWeight: 600, color: 'var(--wl-primary)' }}>Registration Date</span>}
          initialValue={dayjs()}
          rules={[{ required: true }]}
        >
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
        </Form.Item>

        {selectedChild && (
          <div style={{
            background: 'var(--wl-primary-pale)', borderRadius: 10, padding: '12px 16px',
            border: '1px solid var(--wl-primary-light)', marginTop: 4,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <strong style={{ color: 'var(--wl-primary)', fontSize: 14 }}>{selectedChild.name}</strong>
              <Tag style={{ borderRadius: 9999, fontSize: 11, background: 'var(--wl-primary-pale)', color: 'var(--wl-primary)', border: '1px solid var(--wl-primary-light)', fontWeight: 600 }}>
                {selectedChild.gender}{selectedChild.age ? ` · ${selectedChild.age} yrs` : ''}
              </Tag>
            </div>
            <div style={{ fontSize: 12, color: '#666', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span><strong>Parent:</strong> {selectedChild.parent?.name} | {selectedChild.parent?.phone}</span>
              <span><strong>School:</strong> {selectedChild.school?.name || '—'}</span>
            </div>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default RegistrationForm;
